"use client";

import { useState } from "react";
import { format } from "date-fns";
import {
  CheckCircle2,
  XCircle,
  Clock,
  MessageSquare,
  AlertTriangle,
  Send,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  mockApprovals,
  approvalStatusConfig,
  platformIcons,
  mockPosts,
} from "@/lib/mock-data";
import { useClient } from "@/lib/client-context";

export default function ApprovalsPage() {
  const { selectedClientId } = useClient();
  const [expandedId, setExpandedId] = useState<string | null>("apr-1");

  // Filter approvals by client — match via the linked post's clientId
  const clientPostIds = selectedClientId === "all"
    ? null
    : new Set(mockPosts.filter((p) => p.clientId === selectedClientId).map((p) => p.id));

  const filteredApprovals = clientPostIds
    ? mockApprovals.filter((a) => clientPostIds.has(a.postId))
    : mockApprovals;

  const pending = filteredApprovals.filter((a) => a.status === "pending");
  const changesRequested = filteredApprovals.filter(
    (a) => a.status === "changes_requested"
  );
  const approved = filteredApprovals.filter((a) => a.status === "approved");
  const rejected = filteredApprovals.filter((a) => a.status === "rejected");

  const copyReviewLink = (postId: string) => {
    const post = mockPosts.find((p) => p.id === postId);
    if (!post) return;
    const url = `${window.location.origin}/review/${post.clientId}`;
    navigator.clipboard?.writeText(url);
  };

  const renderApprovalCard = (approval: (typeof mockApprovals)[0]) => {
    const status = approvalStatusConfig[approval.status];
    const platform = platformIcons[approval.platform];
    const isExpanded = expandedId === approval.id;

    return (
      <Card
        key={approval.id}
        className={cn(
          "overflow-hidden transition-shadow",
          isExpanded && "shadow-md ring-1 ring-border"
        )}
      >
        {/* Card Header */}
        <button
          onClick={() => setExpandedId(isExpanded ? null : approval.id)}
          className="w-full text-left"
        >
          <div className="flex items-center gap-4 p-5">
            <div className="relative">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                  {approval.authorInitials}
                </AvatarFallback>
              </Avatar>
              <div
                className={cn(
                  "absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-card flex items-center justify-center",
                  status.dot
                )}
              >
                {approval.status === "pending" && (
                  <Clock className="h-3 w-3 text-white" />
                )}
                {approval.status === "approved" && (
                  <CheckCircle2 className="h-3 w-3 text-white" />
                )}
                {approval.status === "rejected" && (
                  <XCircle className="h-3 w-3 text-white" />
                )}
                {approval.status === "changes_requested" && (
                  <AlertTriangle className="h-3 w-3 text-white" />
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-medium truncate">
                  {approval.content.slice(0, 60)}...
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={cn(
                    "text-[10px] font-medium px-1.5 py-0.5 rounded",
                    platform?.bg,
                    platform?.color
                  )}
                >
                  {platform?.label}
                </span>
                <span
                  className={cn(
                    "text-[10px] font-medium px-1.5 py-0.5 rounded",
                    status.bg,
                    status.color
                  )}
                >
                  {status.label}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  by {approval.author}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  &middot; v{approval.version}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Client</p>
                <p className="text-sm font-medium">{approval.client}</p>
              </div>
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-secondary text-secondary-foreground text-[10px] font-semibold">
                  {approval.clientInitials}
                </AvatarFallback>
              </Avatar>
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <>
            <Separator />
            <div className="p-5 grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
              {/* Post Preview */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Post Content
                </p>
                <div className="rounded-lg border p-4 bg-muted/20">
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {approval.content}
                  </p>
                  <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                    {approval.labels.map((label) => (
                      <span
                        key={label.name}
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full text-white"
                        style={{ backgroundColor: label.color }}
                      >
                        {label.name}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="rounded-lg border p-3">
                    <p className="text-[11px] text-muted-foreground">
                      Platform
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      {platform?.label}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[11px] text-muted-foreground">
                      Submitted
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      {format(approval.submittedAt, "MMM d, yyyy")}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-[11px] text-muted-foreground">
                      Version
                    </p>
                    <p className="text-sm font-medium mt-0.5">
                      v{approval.version}
                      {approval.version > 1 && (
                        <span className="text-[11px] text-muted-foreground ml-1">
                          ({approval.version - 1} revision
                          {approval.version > 2 ? "s" : ""})
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Client Response */}
                {approval.note && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Client Response
                    </p>
                    <div
                      className={cn(
                        "rounded-lg border-l-4 p-4",
                        approval.status === "approved" &&
                          "border-l-green-500 bg-green-50/50",
                        approval.status === "rejected" &&
                          "border-l-red-500 bg-red-50/50",
                        approval.status === "changes_requested" &&
                          "border-l-orange-500 bg-orange-50/50"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="bg-secondary text-secondary-foreground text-[9px] font-semibold">
                            {approval.clientInitials}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">
                          {approval.client}
                        </span>
                        {approval.respondedAt && (
                          <span className="text-[11px] text-muted-foreground">
                            {format(approval.respondedAt, "MMM d 'at' h:mm a")}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-foreground/80">
                        {approval.note}
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                {approval.status === "pending" && (
                  <div className="mt-4 flex items-center gap-2">
                    <Button variant="outline" size="sm">
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Send Reminder
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyReviewLink(approval.postId)}
                    >
                      Copy Review Link
                    </Button>
                  </div>
                )}
                {approval.status === "changes_requested" && (
                  <div className="mt-4 flex items-center gap-2">
                    <Button size="sm" className="shadow-sm shadow-primary/25">
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Resubmit for Approval
                    </Button>
                  </div>
                )}
              </div>

              {/* Comments Thread */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Comments ({approval.comments.length})
                </p>
                <div className="space-y-3 max-h-[300px] overflow-auto">
                  {approval.comments.map((comment, i) => (
                    <div
                      key={i}
                      className={cn(
                        "rounded-lg p-3",
                        comment.isClient
                          ? "bg-secondary/60 ml-4"
                          : "bg-muted/40 mr-4"
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Avatar className="h-5 w-5">
                          <AvatarFallback
                            className={cn(
                              "text-[8px] font-semibold",
                              comment.isClient
                                ? "bg-secondary text-secondary-foreground"
                                : "bg-primary/10 text-primary"
                            )}
                          >
                            {comment.initials}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs font-medium">
                          {comment.author}
                        </span>
                        {comment.isClient && (
                          <Badge
                            variant="secondary"
                            className="text-[9px] px-1.5 py-0 h-4"
                          >
                            Client
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm leading-relaxed">{comment.text}</p>
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        {comment.time}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Reply box */}
                <div className="mt-3">
                  <Textarea
                    placeholder="Add a comment..."
                    className="resize-none text-sm min-h-[80px]"
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <Button variant="outline" size="sm" className="text-xs">
                      Internal Note
                    </Button>
                    <Button size="sm" className="text-xs">
                      <Send className="h-3 w-3 mr-1.5" />
                      Send to Client
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Approvals</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {pending.length} awaiting client approval &middot;{" "}
            {changesRequested.length} need revisions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm">
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            Filter by Client
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-8 overflow-auto">
        <Tabs defaultValue="pending">
          <TabsList className="mb-6">
            <TabsTrigger value="pending" className="gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Pending
              {pending.length > 0 && (
                <Badge
                  variant="secondary"
                  className="h-5 min-w-[20px] px-1.5 text-[10px] bg-amber-100 text-amber-700"
                >
                  {pending.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="changes" className="gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              Changes Requested
              {changesRequested.length > 0 && (
                <Badge
                  variant="secondary"
                  className="h-5 min-w-[20px] px-1.5 text-[10px] bg-orange-100 text-orange-700"
                >
                  {changesRequested.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved" className="gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Approved
              <Badge
                variant="secondary"
                className="h-5 min-w-[20px] px-1.5 text-[10px]"
              >
                {approved.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="rejected" className="gap-1.5">
              <XCircle className="h-3.5 w-3.5" />
              Rejected
              <Badge
                variant="secondary"
                className="h-5 min-w-[20px] px-1.5 text-[10px]"
              >
                {rejected.length}
              </Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="space-y-4">
            {pending.map(renderApprovalCard)}
          </TabsContent>

          <TabsContent value="changes" className="space-y-4">
            {changesRequested.map(renderApprovalCard)}
          </TabsContent>

          <TabsContent value="approved" className="space-y-4">
            {approved.map(renderApprovalCard)}
          </TabsContent>

          <TabsContent value="rejected" className="space-y-4">
            {rejected.map(renderApprovalCard)}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
